/**
 * Phase 15 Plan 15-02 — MonthlySummaryPage
 *
 * Route: /logsheets/monthly-summary
 * Permission gate: logsheet.monthly.read
 *
 * Per-machine monthly summary table with month picker, site filter placeholder,
 * CSV + PDF export, and row-click drill-through to MachineMonthlyEntriesPage.
 */

import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { useMemo, useState, type JSX } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { MonthLockedBadge } from '@/components/monthly/MonthLockedBadge';
import { MonthPicker } from '@/components/monthly/MonthPicker';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  downloadMonthlySummaryCsv,
  downloadMonthlySummaryPdf,
  useMonthlySummaryQuery,
} from '@/hooks/useLogsheetMonthlyQueries';
import { useAuth } from '@/lib/auth/AuthContext';
import { formatMachineName } from '@/lib/utils/machine-label';
import type { MonthlySummaryListQuery, MonthlySummaryRow } from '@/types/logsheet-monthly';

function currentMonthStr(): string {
  const d = new Date();
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function formatKpiTotals(kpiTotals: Readonly<Record<string, number>>): string {
  const entries = Object.entries(kpiTotals)
    .map(([k, v]) => `${k}: ${String(v)}`)
    .join(', ');
  if (entries.length > 80) return `${entries.slice(0, 77)}…`;
  return entries;
}

export function MonthlySummaryPage(): JSX.Element {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const [month, setMonth] = useState<string>(currentMonthStr());
  const [csvLoading, setCsvLoading] = useState<boolean>(false);
  const [pdfLoading, setPdfLoading] = useState<boolean>(false);

  const query: MonthlySummaryListQuery = useMemo(
    () => ({ month, page: 1, pageSize: 50 }),
    [month],
  );

  const listQuery = useMonthlySummaryQuery(query);

  const columns = useMemo<ColumnDef<MonthlySummaryRow>[]>(
    () => [
      {
        accessorKey: 'machineCode',
        header: 'Machine Code',
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.machineCode}</span>
        ),
      },
      {
        accessorKey: 'machineName',
        header: 'Machine Name',
        cell: ({ row }) => (
          <span>{formatMachineName(row.original.machineName, row.original.vehicleNumber)}</span>
        ),
      },
      {
        accessorKey: 'totalWorkDays',
        header: 'Work Days',
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.totalWorkDays}</span>
        ),
      },
      {
        accessorKey: 'totalIdleDays',
        header: 'Idle Days',
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.totalIdleDays}</span>
        ),
      },
      {
        // Client feedback (2026-09): Work Days / Idle Days count DATES, so a machine
        // running multiple shifts a day showed no sign of the extra logsheets. This
        // column is the entry count.
        accessorKey: 'totalLogsheets',
        header: 'Logsheets',
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.totalLogsheets}</span>
        ),
      },
      {
        accessorKey: 'pendingEntriesCount',
        header: 'Pending',
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.pendingEntriesCount}</span>
        ),
      },
      {
        id: 'locked',
        header: 'Locked',
        cell: ({ row }) => (
          <div data-testid={row.original.locked ? `monthly-summary-row-${String(row.original.machineId)}-locked-badge` : undefined}>
            <MonthLockedBadge
              locked={row.original.locked}
              month={month}
            />
            {!row.original.locked && (
              <span className="text-xs text-muted-foreground">—</span>
            )}
          </div>
        ),
      },
      {
        id: 'kpiTotals',
        header: 'KPI Totals',
        cell: ({ row }) => (
          <span
            className="text-xs text-muted-foreground"
            title={Object.entries(row.original.kpiTotals)
              .map(([k, v]) => `${k}: ${String(v)}`)
              .join(', ')}
          >
            {formatKpiTotals(row.original.kpiTotals)}
          </span>
        ),
      },
    ],
    [month],
  );

  const data = listQuery.data?.items ?? [];

  const table = useReactTable({
    data: data as MonthlySummaryRow[],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!permissions.includes('logsheet.monthly.read')) {
    return <Navigate to="/dashboard" replace />;
  }

  async function handleExportCsv(): Promise<void> {
    setCsvLoading(true);
    try {
      await downloadMonthlySummaryCsv(query);
    } finally {
      setCsvLoading(false);
    }
  }

  async function handleExportPdf(): Promise<void> {
    setPdfLoading(true);
    try {
      await downloadMonthlySummaryPdf(query);
    } finally {
      setPdfLoading(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="monthly-summary-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Monthly Summary</h1>
          <p className="text-muted-foreground">
            Per-machine monthly logsheet summary with lock status.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => { void handleExportCsv(); }}
            disabled={csvLoading}
            data-testid="monthly-summary-export-csv"
          >
            {csvLoading ? 'Exporting…' : 'Export CSV'}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => { void handleExportPdf(); }}
            disabled={pdfLoading}
            data-testid="monthly-summary-export-pdf"
          >
            {pdfLoading ? 'Exporting…' : 'Export PDF'}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Summary</CardTitle>
          <CardDescription>
            Select a month to view per-machine totals. Click a row to view individual entries.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <span className="text-sm font-medium leading-none">Month</span>
              <div className="mt-1">
                <MonthPicker value={month} onChange={setMonth} />
              </div>
            </div>
            {/* Phase 16: Site filter placeholder */}
            <div className="space-y-1" data-testid="monthly-summary-filter-site">
              {/* TODO Phase 16: wire useSitesQuery */}
            </div>
            {/* Phase 16: Work Order filter placeholder */}
            <div className="space-y-1" data-testid="monthly-summary-filter-workorder">
              {/* TODO Phase 16: wire workOrderId filter */}
            </div>
          </div>

          {listQuery.isLoading && (
            <p className="text-sm text-muted-foreground" data-testid="monthly-summary-loading">
              Loading summary…
            </p>
          )}
          {listQuery.isError && (
            <p className="text-sm text-destructive" data-testid="monthly-summary-error">
              Failed to load monthly summary.
            </p>
          )}

          {listQuery.data && (
            <>
              {data.length === 0 ? (
                <div className="py-8 text-center" data-testid="monthly-summary-empty-state">
                  <p className="text-sm text-muted-foreground">No data for {month}.</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Try a different month or site filter.
                  </p>
                </div>
              ) : (
                <div data-testid="monthly-summary-table">
                  {/* Mobile card list */}
                  <div className="md:hidden space-y-3">
                    {table.getRowModel().rows.map((row) => (
                      <button
                        key={row.id}
                        type="button"
                        onClick={() => {
                          void navigate(
                            `/logsheets/monthly-summary/${String(row.original.machineId)}?month=${month}`,
                          );
                        }}
                        className="w-full text-left rounded-lg border p-4 shadow-sm hover:bg-muted/40 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div>
                            <div className="font-medium text-sm">{formatMachineName(row.original.machineName, row.original.vehicleNumber)}</div>
                            <div className="text-xs text-muted-foreground font-mono">{row.original.machineCode}</div>
                          </div>
                          <div>
                            <MonthLockedBadge locked={row.original.locked} month={month} />
                          </div>
                        </div>
                        {/* Client feedback (2026-09): Logsheets sits alongside the
                            day counts on mobile too, so the card and the desktop
                            table show the same figures. */}
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          <div><span className="font-medium text-foreground">Work: </span><span className="font-mono">{row.original.totalWorkDays}d</span></div>
                          <div><span className="font-medium text-foreground">Idle: </span><span className="font-mono">{row.original.totalIdleDays}d</span></div>
                          <div><span className="font-medium text-foreground">Logsheets: </span><span className="font-mono">{row.original.totalLogsheets}</span></div>
                          <div><span className="font-medium text-foreground">Pending: </span><span className="font-mono">{row.original.pendingEntriesCount}</span></div>
                        </div>
                        {Object.keys(row.original.kpiTotals).length > 0 && (
                          <div className="mt-2 text-xs text-muted-foreground truncate" title={Object.entries(row.original.kpiTotals).map(([k, v]) => `${k}: ${String(v)}`).join(', ')}>
                            {formatKpiTotals(row.original.kpiTotals)}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Desktop table */}
                  <div className="hidden md:block overflow-x-auto">
                    <Table>
                      <TableHeader>
                        {table.getHeaderGroups().map((hg) => (
                          <TableRow key={hg.id}>
                            {hg.headers.map((h) => (
                              <TableHead key={h.id}>
                                {h.isPlaceholder
                                  ? null
                                  : flexRender(h.column.columnDef.header, h.getContext())}
                              </TableHead>
                            ))}
                          </TableRow>
                        ))}
                      </TableHeader>
                      <TableBody>
                        {table.getRowModel().rows.map((row) => (
                          <TableRow
                            key={row.id}
                            data-testid={`monthly-summary-row-${String(row.original.machineId)}`}
                            onClick={() => {
                              void navigate(
                                `/logsheets/monthly-summary/${String(row.original.machineId)}?month=${month}`,
                              );
                            }}
                            className="cursor-pointer hover:bg-muted/40"
                          >
                            {row.getVisibleCells().map((cell) => (
                              <TableCell key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
